import React, { useState, useMemo, useEffect } from 'react';

// Leads List — the campaign's members as a data table (design doc §5.2),
// including campaign-scoped AI columns (§6): AI columns generated here live on
// the campaign (campaign_cards.custom_data), not the candidate's project row.

interface Member {
  id: number;
  entity_type: 'candidate' | 'contact' | 'company';
  entity_id: number;
  stage_id: number;
  stage_name?: string;
  stage_color?: string;
  entity_name?: string;
  entity_title?: string;
  entity_company?: string;
  entity_email?: string;
  entity_personal_email?: string;
  entity_phone?: string;
  entity_photo?: string;
  entity_linkedin?: string;
  custom_data?: Record<string, any>;
}

interface CampaignColumn {
  column_key: string;
  column_label: string;
  column_type: string;
}

type SortKey = 'entity_name' | 'entity_title' | 'entity_company' | 'stage_name';

const CampaignLeadsList: React.FC<{
  campaign: any;
  columns: CampaignColumn[];
  token: string | null;
  campaignId: string;
  onMoveCard: (cardId: number, toStageId: number) => void;
  onRemoveCard: (cardId: number) => void;
  onOpenEntity: (entityType: string, entityId: number) => void;
  onColumnsChanged: () => void;
}> = ({ campaign, columns, token, campaignId, onMoveCard, onRemoveCard, onOpenEntity, onColumnsChanged }) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'entity_name', dir: 'asc' });
  const [showAiModal, setShowAiModal] = useState(false);

  const members: Member[] = useMemo(() => {
    const rows: Member[] = [];
    for (const stage of campaign.stages || []) {
      for (const card of stage.cards || []) {
        rows.push({ ...card, stage_name: stage.name, stage_color: stage.color });
      }
    }
    return rows;
  }, [campaign]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = members;
    if (q) {
      rows = rows.filter(m =>
        [m.entity_name, m.entity_title, m.entity_company, m.entity_email]
          .some(v => (v || '').toLowerCase().includes(q))
      );
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = (a[sort.key] || '').toString().toLowerCase();
      const bv = (b[sort.key] || '').toString().toLowerCase();
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [members, search, sort]);

  const toggleSort = (key: SortKey) => {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const SortHeader: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      onClick={() => toggleSort(k)}
      className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700"
    >
      <span className="inline-flex items-center gap-1">
        {label}{sort.key === k && <span className="text-gray-400">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  return (
    <div className="card overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 p-3 border-b border-gray-200/80">
        <div className="relative flex-1 max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search members by name, title, company, email..."
            className="w-full pl-10 pr-4 py-2 text-sm input"
          />
        </div>
        <span className="text-sm text-gray-400">{filtered.length} of {members.length}</span>
        <div className="flex-1" />
        <button
          onClick={() => setShowAiModal(true)}
          className="btn-secondary text-sm inline-flex items-center gap-1.5"
          title="Generate an AI column over this campaign's members"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
          Create AI Columns
        </button>
      </div>

      {members.length === 0 ? (
        <div className="p-12 text-center text-gray-500">
          <p className="text-sm">No members yet. Add candidates or contacts from the data table (“Add to Campaign” / shortlist).</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200/80">
              <tr>
                <SortHeader label="Name" k="entity_name" />
                <SortHeader label="Title" k="entity_title" />
                <SortHeader label="Company" k="entity_company" />
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                <SortHeader label="Stage" k="stage_name" />
                {columns.map(col => (
                  <th key={col.column_key} className="px-3 py-2 text-left text-xs font-semibold text-purple-500 uppercase tracking-wide" title="AI column">
                    {col.column_label}
                  </th>
                ))}
                <th className="px-3 py-2 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(m => (
                <tr key={m.id} className="hover:bg-gray-50 group">
                  <td className="px-3 py-2">
                    <button onClick={() => onOpenEntity(m.entity_type, m.entity_id)} className="flex items-center gap-2 text-left min-w-0">
                      {m.entity_photo ? (
                        <img src={m.entity_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {(m.entity_name || '?')[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium text-gray-900 hover:text-blue-600 truncate">{m.entity_name || 'Unknown'}</span>
                      {m.entity_linkedin && (
                        <a href={m.entity_linkedin} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] text-blue-500 hover:underline flex-shrink-0">in</a>
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-gray-600 max-w-[220px] truncate">{m.entity_title || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate">{m.entity_company || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">
                    {m.entity_email ? (
                      <span className="truncate">{m.entity_email}</span>
                    ) : m.entity_personal_email ? (
                      <span className="truncate" title="Personal email">{m.entity_personal_email} <span className="text-[9px] text-amber-500">(personal)</span></span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={m.stage_id}
                      onChange={e => onMoveCard(m.id, parseInt(e.target.value))}
                      className="text-xs rounded-md border border-gray-200 bg-white px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      {(campaign.stages || []).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  {columns.map(col => {
                    const cd = m.custom_data || {};
                    const val = cd[col.column_key];
                    const reasoning = cd[`${col.column_key}__reasoning`];
                    return (
                      <td key={col.column_key} className="px-3 py-2 text-gray-700 max-w-[220px] truncate" title={reasoning || ''}>
                        {val === undefined || val === null || val === '' ? <span className="text-gray-300">—</span> : String(val)}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onRemoveCard(m.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500" title="Remove from campaign">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAiModal && (
        <CreateAiColumnModal
          token={token}
          campaignId={campaignId}
          onClose={() => setShowAiModal(false)}
          onDone={() => { setShowAiModal(false); onColumnsChanged(); }}
        />
      )}
    </div>
  );
};


// ─── Create AI Column modal ─────────────────────────────────────────────

const CreateAiColumnModal: React.FC<{
  token: string | null;
  campaignId: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ token, campaignId, onClose, onDone }) => {
  const [fieldsByEntity, setFieldsByEntity] = useState<Record<string, string[]>>({});
  const [entityType, setEntityType] = useState<'candidate' | 'contact'>('candidate');
  const [columnLabel, setColumnLabel] = useState('');
  const [instruction, setInstruction] = useState('');
  const [outputType, setOutputType] = useState<'text' | 'category' | 'score'>('text');
  const [allowedValues, setAllowedValues] = useState('');
  const [scoreMin, setScoreMin] = useState(1);
  const [scoreMax, setScoreMax] = useState(10);
  const [sourceFields, setSourceFields] = useState<string[]>([]);
  const [model, setModel] = useState<'haiku' | 'sonnet'>('haiku');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/campaigns/ai-columns/fields', { headers: { Authorization: `Bearer ${token}` } });
        if (resp.ok) setFieldsByEntity(await resp.json());
      } catch { /* ignore */ }
    })();
  }, [token]);

  const availableFields = fieldsByEntity[entityType] || [];
  const toggleField = (f: string) =>
    setSourceFields(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);

  const pollJob = async (jobId: number) => {
    for (let i = 0; i < 600; i++) {
      await new Promise(r => setTimeout(r, 2000));
      const resp = await fetch(`/api/campaigns/${campaignId}/ai-columns/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) continue;
      const job = await resp.json();
      if (job.total_rows) setProgress(`${job.processed_rows}/${job.total_rows} processed`);
      if (job.status === 'completed') return true;
      if (job.status === 'failed') { setError(job.error || 'Job failed'); return false; }
    }
    return false;
  };

  const handleRun = async () => {
    setError(null);
    if (!columnLabel.trim()) { setError('Column name is required'); return; }
    if (!instruction.trim()) { setError('Instruction is required'); return; }
    if (sourceFields.length === 0) { setError('Pick at least one source field'); return; }
    if (outputType === 'category' && allowedValues.split(',').map(s => s.trim()).filter(Boolean).length < 2) {
      setError('Category columns need at least 2 comma-separated values'); return;
    }
    setRunning(true);
    setProgress('Starting…');
    try {
      const body: any = { entityType, columnLabel, instruction, outputType, sourceFields, model };
      if (outputType === 'category') body.allowedValues = allowedValues.split(',').map(s => s.trim()).filter(Boolean);
      if (outputType === 'score') { body.scoreMin = scoreMin; body.scoreMax = scoreMax; }
      const resp = await fetch(`/api/campaigns/${campaignId}/ai-columns/run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to start');
      const ok = await pollJob(data.jobId);
      if (ok) onDone();
    } catch (err: any) {
      setError(err.message || 'Failed to run');
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={running ? undefined : onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="modal-dialog w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in" onClick={e => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-200/80">
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Create AI Column</h2>
            <p className="text-sm text-gray-500 mt-1">Runs over this campaign's members. Values are stored on the campaign — your project table stays clean.</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apply to</label>
                <select value={entityType} onChange={e => { setEntityType(e.target.value as any); setSourceFields([]); }} className="input text-sm">
                  <option value="candidate">Candidates</option>
                  <option value="contact">Contacts</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <select value={model} onChange={e => setModel(e.target.value as any)} className="input text-sm">
                  <option value="haiku">Haiku (fast)</option>
                  <option value="sonnet">Sonnet (smart)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Column name *</label>
              <input value={columnLabel} onChange={e => setColumnLabel(e.target.value)} placeholder="e.g. Outreach opener" className="input text-sm" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instruction *</label>
              <textarea value={instruction} onChange={e => setInstruction(e.target.value)} rows={3} className="input text-sm resize-none"
                placeholder="e.g. Write a one-line personalised opener referencing their current role and company." />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Output</label>
                <select value={outputType} onChange={e => setOutputType(e.target.value as any)} className="input text-sm">
                  <option value="text">Text</option>
                  <option value="category">Category</option>
                  <option value="score">Score</option>
                </select>
              </div>
              {outputType === 'category' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Values (comma-sep)</label>
                  <input value={allowedValues} onChange={e => setAllowedValues(e.target.value)} placeholder="Yes, No, Maybe" className="input text-sm" />
                </div>
              )}
              {outputType === 'score' && (
                <div className="flex gap-2 items-end">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Min</label><input type="number" value={scoreMin} onChange={e => setScoreMin(parseInt(e.target.value) || 0)} className="input text-sm w-20" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Max</label><input type="number" value={scoreMax} onChange={e => setScoreMax(parseInt(e.target.value) || 0)} className="input text-sm w-20" /></div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source fields *</label>
              <p className="text-xs text-gray-400 mb-2">What the AI reads for each row.</p>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                {availableFields.map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => toggleField(f)}
                    className={`px-2 py-1 rounded-md text-xs border transition-colors ${
                      sourceFields.includes(f) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>}
            {running && progress && <p className="text-sm text-blue-600 bg-blue-50 rounded-md px-3 py-2">Running… {progress}</p>}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} disabled={running} className="btn-ghost text-sm">Cancel</button>
              <button type="button" onClick={handleRun} disabled={running} className="btn-primary text-sm">
                {running ? 'Running…' : 'Run column'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignLeadsList;
